import { ReactNode, useEffect } from "react";
import {
  Dialog,
  DialogBackdrop,
  DialogPanel,
  DialogTitle,
} from "@headlessui/react";
import { useTranslate } from "@made-people/grease-translations";
import { Markdown } from "@made-people/blueprint-markdown";
import clsx from "clsx";
import { Cross } from "../../../assets/icons/Cross";
import { ResolvedOverlayStyling } from "./size-selector-overlay-theme";

export type SizeSelectorOverlayShellProps = {
  isOpen: boolean;
  onClose: () => void;
  styling: ResolvedOverlayStyling;
  headline?: string;
  caption?: string;
  /** Secondary caption rendered below the CTA button. */
  supportingText?: string;
  /** Invoked on CTA click (applies the selected size to the URL). */
  onSubmit: () => void;
  ctaDisabled: boolean;
  error?: string;
  children?: ReactNode;
};

export const SizeSelectorOverlayShell = ({
  isOpen,
  onClose,
  styling,
  headline,
  caption,
  supportingText,
  onSubmit,
  ctaDisabled,
  error,
  children,
}: SizeSelectorOverlayShellProps) => {
  const translate = useTranslate();

  useEffect(() => {
    if (!isOpen) {
      return;
    }
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        onClose();
      }
    };
    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, [isOpen, onClose]);

  const ctaClassName = clsx(
    "mt-4 block w-full rounded-md px-4 py-3 text-center text-button-md transition disabled:opacity-60",
    styling.ctaBackground,
    styling.ctaText,
  );

  return (
    <Dialog open={isOpen} onClose={() => {}} className="relative z-[100]">
      <DialogBackdrop className="fixed inset-0 bg-black/40" />
      <div className="fixed inset-0 flex items-center justify-center p-4">
        <DialogPanel
          className={clsx(
            "relative w-full max-w-[420px] rounded-md p-6 shadow-xl lg:p-8",
            styling.background,
            styling.typography,
          )}
        >
          <button
            type="button"
            onClick={onClose}
            aria-label={translate("Site_Settings_Close_Button_Label")}
            className="absolute right-4 top-4 flex h-8 w-8 items-center justify-center"
          >
            <Cross className="h-3 w-3 fill-current" />
          </button>

          {headline && (
            <DialogTitle className="text-center font-secondary text-heading-md">
              {headline}
            </DialogTitle>
          )}

          {supportingText && (
            <div className="mt-2 text-center text-body-sm">
              <Markdown content={supportingText} />
            </div>
          )}

          {children}

          {error && (
            <p className="mt-2 text-body-2xs text-system-error">{error}</p>
          )}

          <button
            type="button"
            onClick={onSubmit}
            disabled={ctaDisabled}
            className={ctaClassName}
          >
            {translate("Size_Picker_Select_Size")}
          </button>

          {caption && (
            <div className="mt-3 text-center text-body-2xs">
              <Markdown content={caption} />
            </div>
          )}
        </DialogPanel>
      </div>
    </Dialog>
  );
};
